const drawerEvents = {
    OnLineItemUpdate: 'action:cart:updateLineItem',
    onQuantityChange: 'on:QuantityChange',
    triggerCartChange: 'action:cart:updateCart',
};

class DrawerCart extends HTMLElement {
    constructor() {
        super();
        this.currentLineItem;
        this.lineItemError = null;
        this.cartIdentifier = this.getAttribute('render-section');
        this.checkoutContainer = document.querySelector(
            '[cart-checkout-container]'
        );

        this.sectionId =
            this.getAttribute('data-sectionId') || this.cartIdentifier;

        /*
         * Cart requests are intentionally batched and locked here.
         * This prevents multiple cart/change requests from completing out of
         * order and rendering stale totals or stale line-item keys.
         */
        this.pendingLineItemUpdates = new Map();
        this.cartUpdateTimer = null;
        this.cartUpdateInProgress = false;
        this.cartRefreshRequestId = 0;
        this.cartUpdateDelay = 120;

        /* Store bound callbacks so listeners can be removed correctly. */
        this.boundHandleLineItem = this.handleLineItem.bind(this);
        this.boundRenderCart = this.renderCart.bind(this);
        this.boundFetchAndRenderCartItems =
            this.fetchAndRenderCartItems.bind(this);
        this.boundShowCartPopup = this.handleShowCartPopup.bind(this);

        this.addEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.boundHandleLineItem
        );

        /* Listening for this event to update cart items and show cart drawer base on products Added to cart via product-form,product-card component 
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.triggerCartChange, { detail: {}}));
            Note: Make sure to pass the `detail` @object with the data consisting of sections that can fetched via section rendering Api functionality of Shopify
        */

        document.addEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.boundRenderCart
        );

        /* Listening for this event to update cart items and show cart drawer based on custom functionality of the theme 
            Scenarios: When user adds a product to cart, the cart drawer should be updated and shown
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.updateAndShowCart, { detail: {}}));
        */
        document.addEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.boundFetchAndRenderCartItems
        );

        document.addEventListener(
            _EVENT_HELPER.showCartPopup,
            this.boundShowCartPopup
        );
    }

    handleShowCartPopup(e) {
        if (!e?.detail?.sections) return;
        this.renderCartContents(e.detail);
        this._updateCartItemCount(e.detail);
    }

    _getCartItemCount(data) {
        const cartSection = data?.sections?.[this.cartIdentifier];
        if (!cartSection) {
            return Number(this.dataset.cartitemcount || 0);
        }

        const parseCartSectionHTML = new DOMParser().parseFromString(
            cartSection,
            'text/html'
        );
        const cartElement = parseCartSectionHTML.querySelector(this.localName);

        return Number(cartElement?.dataset?.cartitemcount || 0);
    }

    renderCart(e) {
        const data = e?.detail;

        /*
         * Both the cart drawer and cart page listen to the same document event.
         * Ignore events that do not contain the section for this component.
         */
        if (!data?.sections?.[this.cartIdentifier]) return;

        this.renderCartContents(data);
        // Update dependent Elements to be render
        this.renderElementsBasedOnCartItems(data);
        if (
            this.cartIdentifier == 'cart-drawer' &&
            !this.closest('cart-drawer-component')?.isDrawerOpen
        ) {
            HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, this, {
                drawerType: 'cart-drawer',
            });
        }
    }

    _get(property) {
        return this[property];
    }

    _set(property, value) {
        this[property] = value;
    }

    _updateCartItemCount(data) {
        this.cartItemCount = this._getCartItemCount(data);
        this.dataset.cartitemcount = this.cartItemCount;
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerToastMessage,
            document,
            {
                toastType: 'common-errors',
                messageType: messageType,
                message: responseObj.message,
            }
        );
    }

    quantityElementAction({ line, quantity }) {
        const enable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'false')
            );
        };

        const disable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'true')
            );
        };

        const resetToPreviousValue = () => {
            this.querySelector(`[item-index="${line}"]`)
                ?.querySelectorAll('quantity-selector input')
                .forEach((el) => {
                    el.value = (Number(quantity) - 1).toString();
                });
        };

        return {
            enable,
            disable,
            resetToPreviousValue,
        };
    }

    setCartControlsDisabled(disabled) {
        this.toggleAttribute('aria-busy', disabled);

        this.querySelectorAll('quantity-selector').forEach((el) => {
            el.setAttribute('is-disabled', disabled ? 'true' : 'false');
        });

        this.querySelectorAll('[remove-lineItem]').forEach((el) => {
            if (disabled) {
                if (!Object.prototype.hasOwnProperty.call(el.dataset, 'cartPreviousTabindex')) {
                    el.dataset.cartPreviousTabindex = el.getAttribute('tabindex') ?? '';
                }
                if (!Object.prototype.hasOwnProperty.call(el.dataset, 'cartPreviousPointerEvents')) {
                    el.dataset.cartPreviousPointerEvents = el.style.pointerEvents || '';
                }
                el.setAttribute('aria-disabled', 'true');
                el.setAttribute('tabindex', '-1');
                el.style.pointerEvents = 'none';
            } else {
                const hasStoredState =
                    Object.prototype.hasOwnProperty.call(
                        el.dataset,
                        'cartPreviousTabindex'
                    ) ||
                    Object.prototype.hasOwnProperty.call(
                        el.dataset,
                        'cartPreviousPointerEvents'
                    );

                /* Newly rendered controls were never disabled, so leave them intact. */
                if (!hasStoredState) return;

                const previousTabindex = el.dataset.cartPreviousTabindex;
                const previousPointerEvents =
                    el.dataset.cartPreviousPointerEvents;

                el.removeAttribute('aria-disabled');

                if (previousTabindex === '') {
                    el.removeAttribute('tabindex');
                } else {
                    el.setAttribute('tabindex', previousTabindex);
                }

                el.style.pointerEvents = previousPointerEvents || '';
                delete el.dataset.cartPreviousTabindex;
                delete el.dataset.cartPreviousPointerEvents;
            }
        });
    }

    updateCartTotal(data) {
        const cartTotal = data.total_price;
        const cartTotalEl = document.querySelectorAll('[cart-total]');
        [...cartTotalEl].forEach((el) => {
            el.textContent = Shopify.formatMoney(
                cartTotal,
                Shopify.money_format
            );
        });
    }

    cartCheckoutEl() {
        const hideContainerClass = 'hide';
        const show = () => {
            if (this.checkoutContainer.classList.contains(hideContainerClass)) {
                this.checkoutContainer.classList.remove(hideContainerClass);
            }
        };
        const hide = () => {
            this.checkoutContainer.classList.add(hideContainerClass);
        };

        return {
            show,
            hide,
        };
    }

    renderElementsBasedOnCartItems(data) {
        this._updateCartItemCount(data);
        if (this.cartItemCount > 0) {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.showCartRecommendations,
                this
            );
        } else {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.hideCartRecommendations,
                this
            );
        }
    }

    handleLineItem({ detail }) {
        if (!detail?.key || this.cartUpdateInProgress) return;

        const parsedQuantity = Number.parseInt(detail.quantity, 10);
        const quantity = Number.isNaN(parsedQuantity)
            ? 0
            : Math.max(0, parsedQuantity);

        /* Last change for the same line wins; different lines are batched. */
        this.pendingLineItemUpdates.set(detail.key, {
            ...detail,
            key: detail.key,
            quantity,
        });

        /*
         * Removals can be briefly collected into one request. Quantity changes
         * lock all cart controls immediately so Shopify's inventory validation
         * remains on the cart/change endpoint.
         */
        if (quantity === 0) {
            this.quantityElementAction(detail).disable();
        } else {
            this.setCartControlsDisabled(true);
        }

        clearTimeout(this.cartUpdateTimer);
        this.cartUpdateTimer = setTimeout(
            () => this.flushLineItemUpdates(),
            this.cartUpdateDelay
        );
    }

    flushLineItemUpdates() {
        if (
            this.cartUpdateInProgress ||
            this.pendingLineItemUpdates.size === 0
        ) {
            return;
        }

        const queuedUpdates = Array.from(
            this.pendingLineItemUpdates.values()
        );
        this.pendingLineItemUpdates.clear();

        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            this.cartIdentifier
        ).getSectionsString();
        const sectionsUrl = `${window.location.pathname}${window.location.search}`;
        const isRemovalBatch =
            queuedUpdates.length > 1 &&
            queuedUpdates.every((update) => update.quantity === 0);

        let endpoint;
        let data;

        if (isRemovalBatch) {
            const updates = queuedUpdates.reduce((accumulator, update) => {
                accumulator[update.key] = 0;
                return accumulator;
            }, {});

            endpoint = `${window.Shopify.routes.root}cart/update.js`;
            data = {
                updates,
                sections: sectionsToRender,
                sections_url: sectionsUrl,
            };
        } else {
            const update = queuedUpdates[queuedUpdates.length - 1];

            endpoint = `${window.Shopify.routes.root}cart/change.js`;
            data = {
                id: update.key,
                quantity: update.quantity,
                sections: sectionsToRender,
                sections_url: sectionsUrl,
            };
        }

        this.cartUpdateInProgress = true;
        this.setCartControlsDisabled(true);

        HELPER_UTIL.fetchData({
            method: 'POST',
            url: endpoint,
            data: JSON.stringify(data),
            config: {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            },
            onsuccess: (response) => {
                if (!response?.sections) {
                    throw new Error('Cart sections were not returned.');
                }

                if (!response.sections[this.cartIdentifier]) {
                    this.fetchAndRenderCartItems();
                    return;
                }

                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    this,
                    { sections: response.sections }
                );
            },
            onerror: (error) => {
                this.handleCartUpdateError(error, queuedUpdates);
            },
            defaultCb: () => {
                this.cartUpdateInProgress = false;
                this.setCartControlsDisabled(false);
            },
        });
    }

    handleCartUpdateError(error, queuedUpdates) {
        const fallbackError = {
            status: error?.status || 500,
            message: 'Something went wrong. Please try again.',
        };

        const processError = (responseObj = fallbackError) => {
            queuedUpdates.forEach((update) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerlineItemError,
                    this,
                    {
                        errorObj: {
                            lineItem: update.key,
                            responseObj: {
                                ...fallbackError,
                                ...responseObj,
                                message:
                                    responseObj?.message || fallbackError.message,
                            },
                            messageType: 'error',
                        },
                    }
                );
            });

            /* Re-render the server-authoritative cart after any failed mutation. */
            this.fetchAndRenderCartItems();
        };

        if (typeof error?.json === 'function') {
            error
                .json()
                .then(processError)
                .catch(() => processError(fallbackError));
        } else {
            processError(fallbackError);
        }
    }

    renderCartContents({ sections }) {
        try {
            const getSectionsToLoop = HELPER_UTIL.sectionRenderUtil(
                this.cartIdentifier
            ).getSectionsObject();
            getSectionsToLoop.forEach((sectionsObjItem) => {
                // Here sections{@object} is the data from the AJAX call we made to cart
                // sectionsObjItem @{object} is the class property consisting list of sections and their ids

                if (!!sections[sectionsObjItem.sectionId]) {
                    [
                        ...document.querySelectorAll(
                            sectionsObjItem.queryElement
                        ),
                    ].forEach((section) => {
                        const renderedSection = HELPER_UTIL.getHTMLFromString(
                            sections[sectionsObjItem.sectionId],
                            sectionsObjItem.queryElement
                        );

                        if (renderedSection) {
                            section.innerHTML = renderedSection.innerHTML;
                        }
                    });
                }
            });
        } catch (error) {
            console.log(error);
        }
    }

    fetchAndRenderCartItems() {
        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            this.cartIdentifier
        ).getSectionsString();
        const requestId = ++this.cartRefreshRequestId;
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.set('sections', sectionsToRender);
        requestUrl.hash = '';

        /*
         * Use Shopify's Section Rendering API for a read-only refresh.
         * The previous GET request to cart/update.js was a mutation endpoint.
         */
        HELPER_UTIL.fetchData({
            method: 'GET',
            url: requestUrl.toString(),
            config: {
                headers: {
                    Accept: 'application/json',
                },
            },
            onsuccess: (response) => {
                if (requestId !== this.cartRefreshRequestId) return;

                const sections = response?.sections || response;
                if (!sections?.[this.cartIdentifier]) return;

                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    this,
                    { sections }
                );
            },
            onerror: (error) => {
                console.log(error);
            },
        });
    }

    disconnectedCallback() {
        clearTimeout(this.cartUpdateTimer);
        this.pendingLineItemUpdates.clear();

        this.removeEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.boundHandleLineItem
        );
        document.removeEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.boundRenderCart
        );
        document.removeEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.boundFetchAndRenderCartItems
        );
        document.removeEventListener(
            _EVENT_HELPER.showCartPopup,
            this.boundShowCartPopup
        );
    }
}

customElements.get('drawer-cart') ||
    customElements.define('drawer-cart', DrawerCart);

class CartPage extends DrawerCart {
    constructor() {
        super();
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        if (cartItemCount > 0) {
            // this.updateCartTotal(data);
            if (this.checkoutContainer) {
                this.cartCheckoutEl().show();
            }
        } else {
            if (this.checkoutContainer) {
                this.cartCheckoutEl().hide();
            }
        }
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        const toastEl = this.querySelector('toast-message');
        toastEl.toast({
            message: responseObj.message,
            messageType: messageType,
        });
    }
}

customElements.get('cart-page') || customElements.define('cart-page', CartPage);

class DrawerItem extends HTMLElement {
    constructor() {
        super();
        this.itemKey = this.getAttribute('item-key'); // Unique line item key of the product in cart
        this.removeItemElement = this.querySelectorAll('[remove-lineItem]');

        this.boundDispatchLineItemUpdate =
            this.dispatchLineItemUpdate.bind(this);
        this.boundHandleRemoveKeyup = this.handleRemoveKeyup.bind(this);
        this.boundShowLineItemError = this.showLineItemError.bind(this);

        [...this.removeItemElement].forEach((el) => {
            el.addEventListener('click', this.boundDispatchLineItemUpdate);
            el.addEventListener('keyup', this.boundHandleRemoveKeyup);
        });

        this.addEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.boundDispatchLineItemUpdate
        );

        document.addEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.boundShowLineItemError
        );
    }

    handleRemoveKeyup(e) {
        if (e.key === 'Enter') {
            this.dispatchLineItemUpdate(e);
        }
    }

    dispatchLineItemUpdate(e) {
        const actionElement = e?.target?.closest?.('[remove-lineItem]');
        if (actionElement?.getAttribute('aria-disabled') === 'true') return;

        const hasQuantity =
            e?.detail &&
            Object.prototype.hasOwnProperty.call(e.detail, 'quantity');
        const productQuantity = hasQuantity ? e.detail.quantity : '0';
        const detail = {
            key: this.itemKey,
            quantity: String(productQuantity),
        };

        if (detail.quantity === '0') {
            const animatedItem = e?.target?.closest?.('[animation-slide-left]');
            if (animatedItem && animatedItem.dataset.cartRemoving !== 'true') {
                animatedItem.dataset.cartRemoving = 'true';
                Motion.animate(
                    animatedItem,
                    {
                        x: 40,
                        opacity: 0,
                    },
                    {
                        duration: parseFloat(0.3),
                        ease: 'cubic-bezier(.75,0,.175,1)',
                    }
                );
            }
        }

        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.OnLineItemUpdate,
            this,
            detail
        );
    }

    handleLineItemErrorUpdate(errorObj) {
        const customMessageComponent =
            this.querySelector('custom-message') ||
            this.nextElementSibling?.querySelector('custom-message');

        customMessageComponent?.showCustomMessage({
            errorMessage: errorObj.responseObj.message,
            messageType: errorObj.messageType,
        });
    }

    resetQuantitySelector() {
        const quantityInput = this.querySelector('quantity-selector input');
        if (quantityInput) {
            quantityInput.value = this.getAttribute('item-quantity');
        }
    }

    resetRemovalAnimation() {
        const animatedItem = this.closest('[animation-slide-left]');
        if (!animatedItem) return;

        delete animatedItem.dataset.cartRemoving;
        Motion.animate(
            animatedItem,
            {
                x: 0,
                opacity: 1,
            },
            {
                duration: parseFloat(0.2),
                ease: 'cubic-bezier(.75,0,.175,1)',
            }
        );
    }

    showLineItemError(event) {
        const { errorObj } = event.detail;
        if (errorObj && errorObj.lineItem === this.itemKey) {
            this.handleLineItemErrorUpdate(errorObj);
            this.resetQuantitySelector();
            this.resetRemovalAnimation();
        }
    }

    disconnectedCallback() {
        this.removeEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.boundDispatchLineItemUpdate
        );

        [...this.removeItemElement].forEach((el) => {
            el.removeEventListener('click', this.boundDispatchLineItemUpdate);
            el.removeEventListener('keyup', this.boundHandleRemoveKeyup);
        });

        document.removeEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.boundShowLineItemError
        );
    }
}

customElements.get('drawer-item') ||
    customElements.define('drawer-item', DrawerItem);



//More checkout button animation 

class AdditionalCheckoutButtonsAnimation extends HTMLElement {
    constructor() {
        super();
        this.toggleButton = this.querySelector("[toggle-payment-buttons]")
        this.toggleButton?.addEventListener("click", this.togglePaymentButtons.bind(this))
        this.buttonsWrapper = this.querySelector("[checkout-buttons-wrapper]")

    }

    togglePaymentButtons(e) {
        e.preventDefault()
        this.toggleButton.classList.toggle("show-payment-options")
        this.setAttribute("style", `--button-wrapper-height:${this.buttonsWrapper?.firstElementChild.offsetHeight}px`)
        this.buttonsWrapper?.classList.toggle("active")
    }

}

customElements.get('additional-checkout-buttons-animation') ||
    customElements.define("additional-checkout-buttons-animation", AdditionalCheckoutButtonsAnimation)